import { AxiosError } from 'axios';
import Cookies from 'js-cookie';
import { useState, useEffect, useCallback } from 'react';
import axiosInstance from '@libs/axios';
import { apiUrl } from '@constants/url';
import { toast } from 'sonner';
import { completeAuthenticatedSession, clearJarvisSession, hydrateSessionFromMasterUser } from '@utils/auth-session';
import type { CubeSsoLoginPayload, CubeSsoLoginResponse, JarvisSessionUser } from '@/types/cube-sso';

type PasswordLoginResponseData = {
  requireMfa?: boolean;
  sessionToken?: string;
  email?: string;
  id?: number | string;
  name?: string;
  accessToken?: string;
  refreshToken?: string;
  ppjkNpwp?: string;
  ppjkName?: string;
  enabledMfa?: boolean;
};

type PasswordLoginResult =
  | {
      kind: 'mfa_required';
      sessionToken: string;
    }
  | {
      kind: 'authenticated';
      enabledMfa: boolean;
    }
  | null
  | undefined;

export default function useAuth() {
  const [error, setError] = useState('');
  const [isLoading, setLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    // Initialize state immediately to prevent blinking
    // User is only authenticated if they have a token AND MFA is enabled
    const accessToken = Cookies.get('accessToken') || localStorage.getItem('accessToken');
    const mfaEnabled = localStorage.getItem('mfaEnabled');
    return !!accessToken && mfaEnabled === 'true';
  });

  // Memoize authentication check to prevent unnecessary re-renders
  const checkAuthStatus = useCallback(() => {
    const cookieToken = Cookies.get('accessToken');
    const localToken = localStorage.getItem('accessToken');
    const accessToken = cookieToken || localToken;
    const mfaEnabled = localStorage.getItem('mfaEnabled');
    const authStatus = !!accessToken && mfaEnabled === 'true';
    setIsAuthenticated(authStatus);
    return authStatus;
  }, []);

  // Check authentication status on mount
  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

  const login = async <T,>(data: T, callback?: () => void): Promise<PasswordLoginResult> => {
    setLoading(true);
    setError('');

    try {
      const response = await axiosInstance.post<{
        success: boolean;
        data: PasswordLoginResponseData;
        error?: { message?: string };
      }>(apiUrl.login, data);

      if (!response.data.success) {
        const message = response.data.error?.message || 'Login failed';
        setError(message);
        toast.error('Login Failed', { description: message });
        return null;
      }

      // Check if MFA is required
      if (response.data.data.requireMfa && response.data.data.sessionToken) {
        // Don't store tokens yet, just return the response data
        setLoading(false);
        return {
          kind: 'mfa_required',
          sessionToken: response.data.data.sessionToken,
        };
      }

      // Extract email, id, and PPJK data from the nested structure
      const { email, id, name, accessToken, refreshToken, ppjkNpwp, enabledMfa } = response.data.data;

      if (enabledMfa === false) {
        // Older backends omitted `email` on the MFA-setup-required response, so
        // fall back to the email submitted in the form. Without this fallback
        // the missing-data guard threw and the MFA-setup modal never opened.
        const formEmail = (data as { email?: string } | null | undefined)?.email;
        const resolvedEmail = email || formEmail;
        if (!accessToken || !refreshToken || !resolvedEmail) {
          throw new Error('Login response is missing required session data');
        }

        // This is a brand-new user's first login: they have no MFA device, so
        // they never pass through /complete-mfa-login and this is the only
        // chance to persist their identity. Store the full authData (not just
        // the email) and hydrate, otherwise `nomor_ppjk` stays unset until the
        // user logs out and back in.
        completeAuthenticatedSession({
          accessToken,
          refreshToken,
          authData: { email: resolvedEmail, id, name, ppjkNpwp },
          mfaEnabled: false,
        });
        await hydrateSessionFromMasterUser();

        setIsAuthenticated(false);

        return { kind: 'authenticated', enabledMfa: false };
      }

      if (!accessToken || !refreshToken || !email) {
        throw new Error('Login response is missing required session data');
      }

      completeAuthenticatedSession({
        accessToken,
        refreshToken,
        authData: { email, id, name, ppjkNpwp },
      });
      await hydrateSessionFromMasterUser();

      setIsAuthenticated(true);

      // Call the callback after setting authentication state
      callback?.();

      // Return success flag with enabledMfa status
      return { kind: 'authenticated', enabledMfa: true };
    } catch (error) {
      if (!(error instanceof AxiosError)) {
        const message = error instanceof Error ? error.message : 'Unknown error';
        setError(message);
        toast.error('Login Failed', { description: message });
        return;
      }

      const message = error.response?.data?.error?.message || error.response?.data?.message || 'Login failed';
      setError(message);
      toast.error('Login Failed', { description: message });
    } finally {
      setLoading(false);
    }
  };

  const loginWithCubeSso = async (
    payload: CubeSsoLoginPayload,
    callback?: () => void
  ): Promise<{
    success: true;
    user: JarvisSessionUser;
    accessToken: string;
    refreshToken: string;
    source?: string;
    isCCNUser?: boolean;
  } | null> => {
    setLoading(true);
    setError('');

    try {
      const response = await axiosInstance.post<CubeSsoLoginResponse & { error?: { message?: string } }>(
        apiUrl.cubeSso.login,
        payload
      );

      if (!response.data?.success || !response.data?.data?.accessToken || !response.data?.data?.refreshToken) {
        const message = response.data?.error?.message || response.data?.message || 'CUBE SSO login failed';
        setError(message);
        toast.error('Login Failed', { description: message });
        return null;
      }

      const { data } = response.data;
      const user = data.user || {};

      completeAuthenticatedSession({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        authData: {
          id: user.id,
          email: user.email || payload.email,
          name: user.name || user.fullName || payload.fullName,
          ppjkNpwp: user.ppjkNpwp,
          authSource: data.source || user.authSource || payload.authSource,
          externalProvider: user.externalProvider || payload.externalProvider,
          externalSubject: user.externalSubject || payload.externalSubject,
          source: data.source || payload.authSource,
          isCCNUser: data.isCCNUser ?? user.isCCNUser ?? true,
          companyName: user.companyName || payload.companyName,
          companyId: user.companyId || payload.companyId,
        },
        mfaEnabled: true,
      });
      await hydrateSessionFromMasterUser();

      setIsAuthenticated(true);
      callback?.();

      return {
        success: true,
        user,
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        source: data.source,
        isCCNUser: data.isCCNUser,
      };
    } catch (error) {
      if (!(error instanceof AxiosError)) {
        setError('Unknown error');
        toast.error('Login Failed', { description: 'Unknown error' });
        return null;
      }

      const message = error.response?.data?.error?.message || error.response?.data?.message || 'CUBE SSO login failed';
      setError(message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const logout = async (callback?: () => void) => {
    setLoading(true);

    try {
      clearJarvisSession();
      setIsAuthenticated(false);
      setTimeout(() => {
        callback?.();
      }, 50);
    } catch (_error) {
    } finally {
      setLoading(false);
    }
  };

  return {
    login,
    loginWithCubeSso,
    logout,
    isLoading,
    error,
    isAuthenticated,
    setIsAuthenticated,
  };
}
